import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
 Card,
 CardContent,
 CardHeader,
 CardTitle,
} from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
 Loader2,
 Users,
 Download,
 FileText,
 Zap,
 Building2,
 ChevronRight,
 Clock,
} from "lucide-react";
import api from "../../api/client";
import RecruiterPageShell from "../../components/recruiter/RecruiterPageShell";
import JobStatusBadge from "../../components/recruiter/JobStatusBadge";
import RecruiterEmptyState from "../../components/recruiter/RecruiterEmptyState";
import Loader from "../../components/ui/Loader";
import { glassCard, glassCardHover, glassPanel } from "../../utils/glassStyles";

export default function RecruiterJobDetails() {
 const { jobId } = useParams();
 const navigate = useNavigate();

 const [job, setJob] = useState(null);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState("");
 const [actionLoading, setActionLoading] = useState({});

 useEffect(() => {
 fetchJob();
 }, [jobId]);

 const fetchJob = async () => {
 try {
 const res = await api.get(`/recruiter/jobs/${jobId}`);
 setJob(res.data);
 } catch (err) {
 console.error(err);
 setError("Failed to load job details.");
 } finally {
 setLoading(false);
 }
 };

 const downloadResume = (url, filename) => {
 const link = document.createElement("a");
 link.href = url;
 link.download = filename || "resume.pdf";
 link.target = "_blank";
 document.body.appendChild(link);
 link.click();
 document.body.removeChild(link);
 };

 const handleStatusChange = async (appId, status) => {
 setActionLoading((prev) => ({ ...prev, [appId]: true }));
 try {
 // Fix: Remove /status from the path, send status in body
 await api.patch(`/recruiter/applications/${appId}`, { status });
 fetchJob(); // Refresh data after update
 } catch (err) {
 setError(err.response?.data?.message || "Action failed");
 } finally {
 setActionLoading((prev) => ({ ...prev, [appId]: false }));
 }
 };

 if (loading) return <Loader label="Loading job details..." />;

 if (error)
 return (
 <RecruiterPageShell title="Job Details" onBack={() => navigate(-1)}>
 <div className="text-center py-16">
 <p className="text-destructive mb-4">{error}</p>
 <Button onClick={() => navigate(-1)}>Go Back</Button>
 </div>
 </RecruiterPageShell>
 );

 if (!job)
 return (
 <RecruiterPageShell title="Job Details" onBack={() => navigate(-1)}>
 <RecruiterEmptyState title="Job not found" description="This job may have been removed." />
 </RecruiterPageShell>
 );

 return (
 <RecruiterPageShell
 title={job.title}
 subtitle={`${job.companyName || job.recruiter?.companyName || "Company"} • ${job.location}`}
 onBack={() => navigate("/recruiter/dashboard")}
 actions={<JobStatusBadge status={job.status} job={job} />}
 >
 {/* Job Info Card */}
 <Card className={`rounded-2xl mb-8 ${glassCard}`}>
 <CardHeader className="flex flex-col sm:flex-row justify-between gap-6">
 <div className="space-y-1">
 <CardTitle className="text-2xl sm:text-3xl font-bold tracking-tight">
 {job.title}
 </CardTitle>
 <div className="flex items-center gap-2 text-muted-foreground text-sm">
 <Building2 className="w-4 h-4" />
 <span className="font-medium">
 {job.companyName || job.recruiter?.companyName}
 </span>
 <span>•</span>
 <span>{job.location}</span>
 </div>
 </div>
 </CardHeader>

 <CardContent className="space-y-8">
 <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-sm">
 <Detail label="Type" value={job.type} />
 <Detail label="Experience" value={job.experience} />
 <Detail
 label="Salary Range"
 value={
 job.salaryMin && job.salaryMax
 ? `${job.salaryMin} - ${job.salaryMax} ETB`
 : job.salaryMin
 ? `${job.salaryMin}+ ETB`
 : "Not specified"
 }
 />
 <Detail
 label="Deadline"
 value={
 job.deadline
 ? new Date(job.deadline).toLocaleDateString()
 : "—"
 }
 />
 </div>

 <div>
 <h3 className="font-semibold text-lg text-foreground">
 Description
 </h3>
 <p className="text-muted-foreground mt-1 whitespace-pre-line leading-relaxed">
 {job.description}
 </p>
 </div>

 {job.requirements && (
 <div>
 <h3 className="font-semibold text-lg text-foreground">
 Requirements
 </h3>
 <p className="text-muted-foreground mt-1 whitespace-pre-line leading-relaxed">
 {job.requirements}
 </p>
 </div>
 )}

 {/* Tags */}
 {job.tags?.length > 0 && (
 <div>
 <h3 className="font-semibold text-foreground mb-3">Tags</h3>
 <div className="flex flex-wrap gap-2">
 {job.tags.map((tag, i) => (
 <Badge
 key={i}
 variant="secondary"
 className="px-3 py-1 bg-primary/10 text-primary dark:text-primary/80 border-primary/20 dark:border-primary/30"
 >
 {tag}
 </Badge>
 ))}
 </div>
 </div>
 )}
 </CardContent>
 </Card>

 {/* Applicants Section */}
 <Card className={`rounded-2xl ${glassPanel}`}>
 <CardHeader className="border-b border-border/50 pb-6">
 <div className="flex items-center justify-between">
 <CardTitle className="flex items-center gap-2.5 text-xl sm:text-2xl font-black">
 <div className="p-2 rounded-xl bg-primary/100/10 text-primary">
 <Users className="w-6 h-6" />
 </div>
 Applicants
 <Badge variant="secondary" className="ml-2 bg-primary/10 text-primary dark:text-primary/80">
 {job.applications?.length || 0}
 </Badge>
 </CardTitle>
 </div>
 </CardHeader>

 <CardContent className="p-4 sm:p-8 space-y-6">
 {!job.applications || job.applications.length === 0 ? (
 <RecruiterEmptyState
 icon={Users}
 title="No applicants yet"
 description="Candidates will appear here once they apply."
 />
 ) : (
 <div className="grid grid-cols-1 gap-4">
 {job.applications.map((app) => (
 <div
 key={app.id}
 className={`rounded-2xl p-5 sm:p-7 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 transition-all duration-300 ${glassCardHover}`}
 >
 {/* Applicant Info */}
 <div className="flex-1 space-y-4 w-full">
 <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
 <div className="space-y-1">
 <h4 className="text-xl font-black text-foreground group-hover:text-primary transition-colors cursor-pointer flex items-center gap-2"
 onClick={() => navigate(`/recruiter/applications/${app.id}`)}>
 {app.applier?.applierProfile?.fullName || app.applier?.email?.split('@')[0] || "Unknown Applicant"}
 <ChevronRight className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
 </h4>
 <div className="flex items-center gap-2 text-muted-foreground text-sm font-medium">
 <Building2 className="w-3.5 h-3.5" />
 {app.applier?.email}
 </div>
 </div>

 {/* Student Badges */}
 <div className="flex flex-wrap gap-2">
 {app.applier?.applierProfile?.university && (
 <Badge variant="outline" className="bg-blue-500/10/50 border-blue-200 text-blue-700 dark:border-blue-800 dark:text-blue-300 px-3 py-1 text-xs">
 {app.applier.applierProfile.university}
 </Badge>
 )}
 {app.applier?.applierProfile?.gpa && (
 <Badge variant="outline" className="bg-amber-500/10/50 border-amber-200 text-amber-700 dark:border-amber-800 dark:text-amber-300 px-3 py-1 text-xs">
 GPA: {app.applier.applierProfile.gpa}
 </Badge>
 )}
 {app.applier?.applierProfile?.gender && (
 <Badge variant="outline" className="bg-accent/10/50 border-accent/30 text-primary dark:border-accent/30 dark:text-accent px-3 py-1 text-xs">
 {app.applier.applierProfile.gender}
 </Badge>
 )}
 </div>
 </div>

 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
 {app.coverLetter && (
 <div className="p-4 rounded-xl border border-border/40">
 <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest mb-2 flex items-center gap-1.5">
 <FileText className="w-3 h-3 text-primary" /> Cover Letter
 </p>
 <p className="text-sm text-foreground/80 line-clamp-3 leading-relaxed">
 {app.coverLetter}
 </p>
 </div>
 )}

 {app.whyMe && (
 <div className="p-4 bg-primary/10/30 rounded-xl border border-primary/20/50 dark:border-primary/30/30">
 <p className="text-[10px] font-black text-primary dark:text-accent uppercase tracking-widest mb-2 flex items-center gap-1.5">
 <Zap className="w-3 h-3" /> Why Job?
 </p>
 <p className="text-sm text-foreground/80 line-clamp-3 leading-relaxed">
 {app.whyMe}
 </p>
 </div>
 )}
 </div>

 <div className="flex items-center gap-2 text-[10px] text-muted-foreground font-bold uppercase tracking-widest">
 <Clock className="w-3 h-3" />
 Applied {new Date(app.appliedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
 </div>
 </div>

 {/* Actions */}
 <div className="flex flex-col sm:flex-row lg:flex-col items-center gap-3 w-full lg:w-48 lg:border-l lg:border-border/50 lg:pl-6">
 {app.applier?.applierProfile?.resumeUrl && (
 <Button
 variant="outline"
 size="sm"
 className="w-full h-11 rounded-xl border-primary/20 hover:bg-primary/10 dark:border-primary/30 dark:hover:bg-primary/20 text-primary dark:text-accent font-bold transition-all shadow-sm"
 onClick={() => {
 const rawUrl = app.applier.applierProfile.resumeUrl;
 const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/$/, "");
 const normalizedPath = rawUrl.startsWith("/") ? rawUrl : `/${rawUrl}`;
 const fullUrl = rawUrl.startsWith('http') ? rawUrl : `${baseUrl}${normalizedPath}`;
 window.open(fullUrl, '_blank');
 }}
 >
 <Download className="w-4 h-4 mr-2" /> Download Resume
 </Button>
 )}

 <div className="w-full space-y-1.5">
 <p className="text-[10px] text-muted-foreground font-black uppercase tracking-widest px-1">Update Status</p>
 <select
 value={app.status}
 onChange={(e) =>
 handleStatusChange(app.id, e.target.value)
 }
 disabled={actionLoading[app.id]}
 className={`w-full px-4 h-11 rounded-xl text-sm border font-bold shadow-sm transition-all cursor-pointer focus:ring-2 focus:ring-primary/50 appearance-none bg-no-repeat bg-[right_1rem_center] ${
 app.status === 'ACCEPTED' ? 'bg-primary/10 border-primary/20 text-primary dark:border-primary/30 dark:text-accent' :
 app.status === 'REJECTED' ? 'bg-rose-500/10 border-rose-200 text-rose-700 dark:border-rose-800 dark:text-rose-300' :
 ' border-border text-foreground hover:border-accent/40'
 }`}
 style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='currentColor'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='C19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundSize: '1em' }}
 >
 <option value="PENDING">Pending Review</option>
 <option value="ACCEPTED">Accepted</option>
 <option value="REJECTED">Rejected</option>
 </select>
 </div>

 {actionLoading[app.id] && (
 <div className="flex items-center gap-2 text-xs text-primary animate-pulse font-bold">
 <Loader2 className="w-3 h-3 animate-spin" />
 Updating...
 </div>
 )}
 </div>
 </div>
 ))}
 </div>
 )}
 </CardContent>
 </Card>
 </RecruiterPageShell>
 );
}

function Detail({ label, value }) {
 return (
 <div className="space-y-0.5">
 <p className="text-muted-foreground text-xs uppercase tracking-wider font-semibold">
 {label}
 </p>
 <p className="font-medium text-foreground">{value}</p>
 </div>
 );
}
